export const DEFAULT_TIMEZONE = "Asia/Kolkata";

export function normalizeTimezone(value?: string | null): string {
  const raw = String(value || "").trim();
  const cleaned = raw.includes("(") ? raw.split("(", 1)[0].trim() : raw;
  if (!cleaned) return DEFAULT_TIMEZONE;
  if (cleaned === "Asia/Calcutta") return "Asia/Kolkata";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: cleaned }).format();
    return cleaned;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

const LEGACY_SQL_DATETIME = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
const ISO_WITHOUT_ZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

export function parseInstantSafe(value?: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  const legacyMatch = raw.match(LEGACY_SQL_DATETIME);
  if (legacyMatch) {
    const [, y, m, d, hh, mm, ss] = legacyMatch;
    // Legacy SQL datetime strings in TechLadder (e.g. call_summaries3.timestamp)
    // are wall-clock timestamps stored by workers in Asia/Kolkata (IST = UTC+5:30).
    // Convert the IST wall-clock parts to a true UTC instant by subtracting the 5:30 offset.
    const utcMs = Date.UTC(+y, +m - 1, +d, +hh, +mm, +ss) - (5 * 60 + 30) * 60 * 1000;
    const date = new Date(utcMs);
    return isNaN(date.getTime()) ? null : date;
  }

  if (ISO_WITHOUT_ZONE.test(raw)) {
    const date = new Date(`${raw}Z`);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

export type AmbiguousDstPolicy = "earlier" | "later";

/**
 * Resolves a wall-clock date and time in a specific IANA timezone into a UTC Date instant.
 *
 * DST Policy:
 * 1. Nonexistent local times (gap / spring-forward, e.g. 02:30 in America/New_York
 *    or 01:30 in Europe/London during transition):
 *    Strictly rejected by returning null. Nonexistent times are never silently accepted
 *    or shifted to arbitrary instants.
 * 2. Ambiguous local times (fold / fall-back, e.g. 01:30 in America/New_York
 *    or Europe/London during transition):
 *    Resolved using an explicit, deterministic policy: the earlier UTC instant
 *    ('earlier' by default, first occurrence / daylight saving time).
 *    Callers may explicitly pass ambiguousPolicy: 'later' if the later occurrence is desired.
 */
export function resolveWallClockToUtc(
  dateStr: string,
  timeStr: string,
  timeZone?: string | null,
  ambiguousPolicy: AmbiguousDstPolicy = "earlier",
): Date | null {
  if (!dateStr || !timeStr) return null;
  const tz = normalizeTimezone(timeZone);
  const dateParts = dateStr.trim().split("-").map(Number);
  if (dateParts.length !== 3 || dateParts.some(isNaN)) return null;
  const [y, m, d] = dateParts;

  const cleanTime = timeStr.trim();
  const timeParts = cleanTime.split(":").map(Number);
  if (timeParts.length < 2 || timeParts.some(isNaN)) return null;
  const hh = timeParts[0] ?? 0;
  const mm = timeParts[1] ?? 0;
  const ss = timeParts[2] ?? 0;

  const targetUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss);
  if (isNaN(targetUtcMs)) return null;

  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hourCycle: "h23",
    });

    const getWallParts = (instant: Date) => {
      const parts = dtf.formatToParts(instant);
      let yr = 0, mo = 1, da = 1, hr = 0, min = 0, sec = 0;
      for (const part of parts) {
        if (part.type === "year") yr = Number(part.value);
        else if (part.type === "month") mo = Number(part.value);
        else if (part.type === "day") da = Number(part.value);
        else if (part.type === "hour") hr = Number(part.value);
        else if (part.type === "minute") min = Number(part.value);
        else if (part.type === "second") sec = Number(part.value);
      }
      return { yr, mo, da, hr, min, sec };
    };

    const matches = (ms: number): boolean => {
      const p = getWallParts(new Date(ms));
      return (
        p.yr === y &&
        p.mo === m &&
        p.da === d &&
        p.hr === hh &&
        p.min === mm &&
        (timeParts.length < 3 || p.sec === ss)
      );
    };

    const candidateSet = new Set<number>();

    // Initial iterative convergence from targetUtcMs
    let c1 = targetUtcMs;
    for (let i = 0; i < 4; i++) {
      const p = getWallParts(new Date(c1));
      const wallMs = Date.UTC(p.yr, p.mo - 1, p.da, p.hr, p.min, p.sec);
      const diff = wallMs - targetUtcMs;
      c1 -= diff;
    }
    if (matches(c1)) candidateSet.add(c1);

    // Check offsets around DST transitions to capture ambiguous times (folds)
    const foldOffsets = [-3600000, 3600000, -1800000, 1800000];
    for (const off of foldOffsets) {
      const testMs = c1 + off;
      if (matches(testMs)) candidateSet.add(testMs);
    }

    const validCandidates = Array.from(candidateSet).sort((a, b) => a - b);

    if (validCandidates.length === 0) {
      // Nonexistent local time (spring-forward gap) -> strict rejection
      return null;
    }

    if (validCandidates.length > 1) {
      // Ambiguous local time (fall-back fold) -> explicit policy: 'earlier' (default) or 'later'
      const chosen =
        ambiguousPolicy === "later"
          ? validCandidates[validCandidates.length - 1]
          : validCandidates[0];
      return new Date(chosen);
    }

    return new Date(validCandidates[0]);
  } catch {
    return null;
  }
}

export function getCallbackInstant(
  date: string,
  time: string,
  callbackTimezone?: string | null,
  scheduledForUtc?: string | Date | null,
): Date | null {
  if (scheduledForUtc) {
    const instant = parseInstantSafe(scheduledForUtc);
    if (instant) return instant;
  }
  if (!date || !time) return null;
  const cbTz = normalizeTimezone(callbackTimezone);
  return resolveWallClockToUtc(date, time, cbTz);
}

export function combineDateTime(date: string, time: string): Date {
  return new Date(`${date}T${time.length === 5 ? time : `${time}:00`}`);
}

export function isWithinNextHour(target: Date, now: Date = new Date()): boolean {
  const diff = target.getTime() - now.getTime();
  return diff > 0 && diff <= 60 * 60 * 1000;
}

export function formatDuration(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = Math.floor(totalSec % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatTimestamp(value?: string | Date | null, timeZone?: string | null): string {
  const date = parseInstantSafe(value);
  if (!date) return "-";
  const tz = normalizeTimezone(timeZone);
  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date);
}

export function formatDateOnly(value?: string | Date | null, timeZone?: string | null): string {
  const date = parseInstantSafe(value);
  if (!date) return "-";
  const tz = normalizeTimezone(timeZone);
  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: tz,
  }).format(date);
}

export function formatTimeOnly(value?: string | Date | null, timeZone?: string | null): string {
  const date = parseInstantSafe(value);
  if (!date) return "-";
  const tz = normalizeTimezone(timeZone);
  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date);
}

export interface CallbackWhenParams {
  date: string;
  time: string;
  callbackTimezone?: string | null;
  displayTimezone?: string | null;
  scheduledForUtc?: string | Date | null;
}

export function formatCallbackWhen(
  dateOrParams: string | CallbackWhenParams,
  time?: string,
  callbackTimezone?: string | null,
  displayTimezone?: string | null,
  scheduledForUtc?: string | Date | null,
): string {
  let d: string;
  let t: string;
  let cbTz: string | null | undefined;
  let dispTz: string | null | undefined;
  let schedUtc: string | Date | null | undefined;

  if (typeof dateOrParams === "object" && dateOrParams !== null) {
    d = dateOrParams.date;
    t = dateOrParams.time;
    cbTz = dateOrParams.callbackTimezone;
    dispTz = dateOrParams.displayTimezone;
    schedUtc = dateOrParams.scheduledForUtc;
  } else {
    d = dateOrParams;
    t = time || "";
    cbTz = callbackTimezone;
    dispTz = displayTimezone;
    schedUtc = scheduledForUtc;
  }

  const instant = getCallbackInstant(d, t, cbTz, schedUtc);
  if (!instant) return `${d} ${t}`.trim() || "-";
  const targetTz = normalizeTimezone(dispTz);
  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: targetTz,
  }).format(instant);
}

/**
 * Computes wall-clock schedule_date and schedule_time in the requested timezone
 * for an immediate execution offset (default 2 minutes lead-time).
 * Formats directly in target timezone so Core reconstructs the exact intended instant.
 */
export function getScheduleNowWallClock(
  timeZone?: string | null,
  leadMinutes: number = 2,
): { scheduleDate: string; scheduleTime: string } {
  const tz = normalizeTimezone(timeZone);
  const targetInstant = new Date(Date.now() + leadMinutes * 60 * 1000);
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      hourCycle: "h23",
    });
    const parts = formatter.formatToParts(targetInstant);
    const partMap: Record<string, string> = {};
    for (const p of parts) partMap[p.type] = p.value;
    return {
      scheduleDate: `${partMap.year}-${partMap.month}-${partMap.day}`,
      scheduleTime: `${partMap.hour}:${partMap.minute}`,
    };
  } catch {
    const y = targetInstant.getUTCFullYear();
    const m = String(targetInstant.getUTCMonth() + 1).padStart(2, "0");
    const d = String(targetInstant.getUTCDate()).padStart(2, "0");
    const hh = String(targetInstant.getUTCHours()).padStart(2, "0");
    const mm = String(targetInstant.getUTCMinutes()).padStart(2, "0");
    return {
      scheduleDate: `${y}-${m}-${d}`,
      scheduleTime: `${hh}:${mm}`,
    };
  }
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function monthLabel(d: Date, timeZone?: string | null): string {
  const tz = normalizeTimezone(timeZone);
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: tz,
  }).format(d);
}

/** Leading nulls for weekday offset, then one Date per day. */
export function buildMonthGrid(monthDate: Date): (Date | null)[] {
  const y = monthDate.getFullYear(), m = monthDate.getMonth();
  const grid: (Date | null)[] = Array.from({ length: new Date(y, m, 1).getDay() }, () => null);
  const days = new Date(y, m + 1, 0).getDate();
  for (let d = 1; d <= days; d++) grid.push(new Date(y, m, d));
  return grid;
}

export function formatRangeLabel(start: Date, end: Date, timeZone?: string | null): string {
  const tz = normalizeTimezone(timeZone);
  const f = (d: Date) =>
    new Intl.DateTimeFormat("en-IN", {
      month: "short",
      day: "2-digit",
      timeZone: tz,
    }).format(d);
  const endYear = new Intl.DateTimeFormat("en-IN", {
    year: "numeric",
    timeZone: tz,
  }).format(end);
  return `${f(start)} – ${f(end)}, ${endYear}`;
}

export function formatHours(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
